/**
 * TanStack Query hooks for Ritual service
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createRitual,
  getActiveRitual,
  getRitual,
  getRitualHistory,
  updateRitualStage,
  completeRitual,
  getCurrentStreak,
  type CreateRitualInput,
  type UpdateRitualStageInput,
  type CompleteRitualInput,
  type RitualStage,
  type RitualMode,
} from '@/lib/services/ritual';

// Re-export types for convenience
export type { RitualStage, RitualMode };

// Query keys
export const ritualKeys = {
  all: ['rituals'] as const,
  active: () => [...ritualKeys.all, 'active'] as const,
  detail: (id: string) => [...ritualKeys.all, 'detail', id] as const,
  history: () => [...ritualKeys.all, 'history'] as const,
  streak: () => [...ritualKeys.all, 'streak'] as const,
};

/**
 * Get active ritual
 */
export function useActiveRitual() {
  return useQuery({
    queryKey: ritualKeys.active(),
    queryFn: getActiveRitual,
    staleTime: 1000 * 60, // 1 minute
    retry: false, // Don't retry on error - services return null/defaults
  });
}

/**
 * Get ritual by ID
 */
export function useRitual(ritualId: string | null) {
  return useQuery({
    queryKey: ritualKeys.detail(ritualId || ''),
    queryFn: () => (ritualId ? getRitual(ritualId) : null),
    enabled: !!ritualId,
    staleTime: 1000 * 60, // 1 minute
  });
}

/**
 * Get ritual history
 */
export function useRitualHistory(limit = 30) {
  return useQuery({
    queryKey: [...ritualKeys.history(), limit],
    queryFn: () => getRitualHistory(limit),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get current streak
 */
export function useCurrentStreak() {
  return useQuery({
    queryKey: ritualKeys.streak(),
    queryFn: getCurrentStreak,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false, // Don't retry on error - services return 0
  });
}

/**
 * Create ritual mutation
 */
export function useCreateRitual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateRitualInput) => createRitual(input),
    onSuccess: (data) => {
      // Invalidate all ritual queries to refetch
      queryClient.invalidateQueries({ queryKey: ritualKeys.all });
      // Also set the active ritual data immediately for instant UI update
      queryClient.setQueryData(ritualKeys.active(), data);
    },
    onError: (error) => {
      console.error('Failed to create ritual:', error);
    },
  });
}

/**
 * Update ritual stage mutation
 */
export function useUpdateRitualStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ritualId, input }: { ritualId: string; input: UpdateRitualStageInput }) =>
      updateRitualStage(ritualId, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ritualKeys.all });
      queryClient.setQueryData(ritualKeys.detail(data.id), data);
    },
  });
}

/**
 * Complete ritual mutation
 */
export function useCompleteRitual() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ritualId, input }: { ritualId: string; input?: CompleteRitualInput }) =>
      completeRitual(ritualId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ritualKeys.all });
      queryClient.invalidateQueries({ queryKey: ritualKeys.streak() });
    },
  });
}
